import _ from 'lodash'
import {TradingRoles, UNITED_STATES} from '@/util/constants'

const METAL_GOLD = `gold`
const METAL_SILVER = `silver`
const METAL_PLATINUM = `platinum`
const METAL_PALLADIUM = `palladium`
const METAL_OTHER = `other`
const METALS = [METAL_GOLD, METAL_SILVER, METAL_PLATINUM, METAL_PALLADIUM]

/**
 *
 * @param node
 * @param path
 * @param result
 * @returns {*[]}
 */
function traverse(node, path = [], result = []) {
  if (!node.children || !node.children.length)
    result.push(
      path.concat({href: `#/trade/category/${node.id}`, text: node.name})
    )
  for (const child of node.children) {
    traverse(
      child,
      path.concat({
        href: `#/trade/category/${node.id}`,
        text: node.name,
      }),
      result
    )
  }
  return result
}

export default {
  methods: {
    getProductMarkups(product, company) {
      const {
        markupBidPremium,
        markupBidPriceFormat,
        markupAskPremium,
        markupAskPriceFormat,
      } = company
      const companyMarkups = {
        bidPremium: markupBidPremium,
        bidPriceFormat: markupBidPriceFormat,
        askPremium: markupAskPremium,
        askPriceFormat: markupAskPriceFormat,
      }
      return _.get(product, `salesRepMarkup[0]`, companyMarkups)
    },
    fixNumber(num, spaces = 2) {
      const rounder = Math.pow(10, spaces)
      const actualNumber =
        typeof num === `string` ? parseFloat(num.replace(/,/g, ``)) : num
      return Math.round(actualNumber * rounder) / rounder
    },
    flattenTree(categoryTree) {
      const lines = []
      for (let i in categoryTree) {
        if (typeof categoryTree[i] === `object` && categoryTree[i].id)
          lines.push(traverse(categoryTree[i], [], []))
      }
      return _.flatten(lines)
    },
    treeSort(categories) {
      const categoriesClone = [...categories]
      // Sorting comparator
      const sorter = (a, b) => {
        if (a.categoryOrdering > b.categoryOrdering) return 1;
        if (a.categoryOrdering < b.categoryOrdering) return -1;
        return 0;
      }
      // Recursive sort
      const sortNodesAndChildren = (nodes) => {
        nodes.sort(sorter)
        nodes.forEach(function (node) {
          if (node.children.length > 0) {
            nodes.children = sortNodesAndChildren(node.children)
          }
        })
        return nodes
      }
      // Build tree
      const items = {
        items: [],
      }
      const map = new Map()
      while (categoriesClone.length > 0) {
        const category = categoriesClone.shift()
        if (category.parentCategoryId === null) {
          items.items.push({
            id: category.id,
            name: category.name,
            categoryOrdering: category.categoryOrdering,
            children: [],
          })
          map.set(category.id, `items[${items.items.length - 1}]children`)
        } else if (map.has(category.parentCategoryId)) {
          const children = _.get(items, map.get(category.parentCategoryId))
          children.push({
            id: category.id,
            name: category.name,
            categoryOrdering: category.categoryOrdering,
            children: [],
          })
          _.set(items, map.get(category.parentCategoryId), children)
          map.set(
            category.id,
            `${map.get(category.parentCategoryId)}[${
              children.length - 1
            }]children`
          )
        } else {
          category.depth = category.depth === undefined ? 1 : category.depth + 1
          if (category.depth > 5) {
            throw new Error(`Maximum depth exceeded`)
          } else {
            categoriesClone.push(category)
          }
        }
      }
      return sortNodesAndChildren(items.items)
    },
    toCurrency(value) {
      if (typeof value !== 'number') {
        value = parseFloat(value)
      }
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      })
      return formatter.format(value)
    },
    getPremiumText(item, buySell) {
      const premium = buySell === 'buy' ? item.askPremium : item.bidPremium
      if (!premium) return `N/A`
      const priceFormat =
        buySell === 'buy' ? item.askPriceFormat : item.bidPriceFormat
      const sign = premium < 0 ? `` : `+`
      switch (priceFormat) {
        case '%':
          return `${sign}${parseFloat(premium).toFixed(2)}%`
        case '$':
          return item.disablePerOzCalc
            ? `${sign}${this.toCurrency(premium)} per pc`
            : `${sign}${this.toCurrency(premium)} per oz`
        case 'Fixed':
          return `Fixed`
        default:
          return 'Unknown'
      }
    },
    getMetalPrimaryContent(item) {
      for (let i in METALS) {
        if (_.get(item, `metalContent.${METALS[i]}`, 0) > 0) {
          return METALS[i]
        }
      }
      return METAL_OTHER
    },
    getQtyDiscountLinesFromItem(item, buySell) {
      return item.pricingTierPrice
        .filter((x) => {
          if (!buySell) return x.activeVolumeBidDiscount || x.activeVolumeAskDiscount && +item.sellQuantity >= +x.qtyDiscLowRange
          if (buySell === `sell`) return x.activeVolumeBidDiscount
          if (buySell === `buy`) {
            return x.activeVolumeAskDiscount && +item.sellQuantity >= +x.qtyDiscLowRange
          }
          return false
        })
        .sort((a, b) =>
          parseFloat(a.qtyDiscLowRange) < parseFloat(b.qtyDiscLowRange) ? -1 : 1
        )
    },
    getQtyDiscountLinesFromItemPriced(item, buySell, company, spotPrices) {
      const pricingTierPrices = this.getQtyDiscountLinesFromItem(
        item,
        buySell
      ).map((discount) => {
        const merged = Object.assign({}, item, discount, {
          bidPremium: discount.volumeBidPremium,
          askPremium: discount.volumeAskPremium,
        })
        Object.assign(merged, this.computePrices(merged, spotPrices, company))
        merged.askPremiumText = this.getPremiumText(merged, `buy`)
        merged.bidPremiumText = this.getPremiumText(merged, `sell`)
        return merged
      })
      return pricingTierPrices
    },
    getHandlingFee(items, country = UNITED_STATES, Countries) {
      const silverWeight = items.reduce((accumulator, item) => {
        accumulator +=
          this.getMetalPrimaryContent(item.item) === METAL_SILVER
            ? item.quantity * item.item.metalContent[METAL_SILVER]
            : 0
        return accumulator
      }, 0)
      const preciousWeight = items.reduce((accumulator, item) => {
        accumulator +=
          [METAL_GOLD, METAL_PLATINUM, METAL_PALLADIUM].indexOf(
            this.getMetalPrimaryContent(item.item)
          ) > -1
            ? item.quantity *
            item.item.metalContent[this.getMetalPrimaryContent(item.item)]
            : 0
        return accumulator
      }, 0)
      const silverWaived = country !== UNITED_STATES ? 5000 : 300
      const preciousWaived = country !== UNITED_STATES ? 100 : 10
      const handlingFee = Countries.reduce((accumulator, countryObj) => {
        accumulator +=
          countryObj.code === country || countryObj.name === country
            ? countryObj.handlingFee
            : 0
        return accumulator
      }, 0)
      return silverWeight >= silverWaived || preciousWeight >= preciousWaived
        ? 0
        : handlingFee
    },
    computePrices(
      item,
      spotPrices,
      company,
      tradingRole = TradingRoles.AUTHORIZED_TRADER,
      userConfiguredMarkups = {}
    ) {
      let bid = parseFloat(item.bidPremium)
      let ask = parseFloat(item.askPremium)
      const metalContent = this.getMetalPrimaryContent(item)
      if (metalContent !== METAL_OTHER) {
        const spotPriceBid = parseFloat(
          spotPrices[_.capitalize(metalContent)].bid
        )
        const spotPriceAsk = parseFloat(
          spotPrices[_.capitalize(metalContent)].ask
        )
        const metalWeight = parseFloat(item.metalContent[metalContent])
        let ozSpotAsk = this.fixNumber(metalWeight * spotPriceAsk, 4)
        let ozSpotBid = this.fixNumber(metalWeight * spotPriceBid, 4)
        switch (item.bidPriceFormat) {
          case '$':
            bid = item.disablePerOzCalc
              ? ozSpotBid + bid
              : ozSpotBid + bid * metalWeight
            break
          case '%':
            bid = ozSpotBid + ozSpotBid * (bid / 100)
            break
        }
        switch (item.askPriceFormat) {
          case '$':
            ask = item.disablePerOzCalc
              ? ozSpotAsk + ask
              : ozSpotAsk + ask * metalWeight
            break
          case '%':
            ask = ozSpotAsk + ozSpotAsk * (ask / 100)
            break
        }
      }
      ask = this.fixNumber(ask)
      bid = this.fixNumber(bid)
      if (tradingRole === TradingRoles.SALES_REP) {
        //Apply markups for sales rep
        const {bidPremium, askPremium, bidPriceFormat, askPriceFormat} =
          Object.assign(
            this.getProductMarkups(item, company),
            userConfiguredMarkups
          )
        bid =
          bidPriceFormat === `$`
            ? this.fixNumber(bid + parseFloat(bidPremium))
            : this.fixNumber(bid * (1 + parseFloat(bidPremium) / 100))
        ask =
          askPriceFormat === `$`
            ? this.fixNumber(ask + parseFloat(askPremium))
            : this.fixNumber(ask * (1 + parseFloat(askPremium) / 100))
      }
      return {
        ask,
        bid,
      }
    },
    /**
     *
     * @param item
     */
    getRetailMarkups(item, company, buySell) {
      //Apply markups for sales rep
      const {bidPremium, askPremium, bidPriceFormat, askPriceFormat} =
        this.getProductMarkups(item, company)
      if (buySell === `buy`)
        return {
          retailFlatMarkup: askPriceFormat === `$` ? askPremium : null,
          retailPercentMarkup: askPriceFormat === `%` ? askPremium : null,
        }
      else
        return {
          retailFlatMarkup: bidPriceFormat === `$` ? bidPremium : null,
          retailPercentMarkup: bidPriceFormat === `%` ? bidPremium : null,
        }
    },
    /**
     *
     * @param buySell
     * @param retailFlatMarkup
     * @param retailPercentMarkup
     * @returns {{askPremium: (null|*|null), askPriceFormat: (null|*|null)}|{bidPriceFormat: (null|*|null), bidPremium: (null|*|null)}}
     */
    convertRetailMarkupsToProductMarkups(
      buySell,
      retailFlatMarkup = 0,
      retailPercentMarkup = 0
    ) {
      if (buySell === `buy`)
        return {
          askPremium:
            retailPercentMarkup > 0 ? retailPercentMarkup : retailFlatMarkup,
          askPriceFormat: retailPercentMarkup > 0 ? `%` : `$`,
        }
      else
        return {
          bidPremium:
            retailPercentMarkup > 0 ? retailPercentMarkup : retailFlatMarkup,
          bidPriceFormat: retailPercentMarkup > 0 ? `%` : `$`,
        }
    },
    /**
     * Renders in plain english how the price was calculated.
     * This method is not currently in use, but keeping it here in case the need arises.
     * @param item
     * @param bidAsk
     * @param spotPrices
     * @returns {string}
     */
    renderFormula(item, bidAsk, spotPrices) {
      const metalContent = this.getMetalPrimaryContent(item)
      let premium =
        bidAsk === `bid`
          ? parseFloat(item.bidPremium)
          : parseFloat(item.askPremium)
      const priceFormat =
        bidAsk === `bid` ? item.bidPriceFormat : item.askPriceFormat
      if (metalContent !== METAL_OTHER && priceFormat !== `Fixed`) {
        // Spot Line
        let ozSpot =
          parseFloat(item.metalContent[metalContent]) *
          parseFloat(spotPrices[_.capitalize(metalContent)][bidAsk])
        const spotLine = `<div>Spot (${_.capitalize(
          metalContent
        )}): ${this.toCurrency(
          spotPrices[_.capitalize(metalContent)][bidAsk]
        )}/oz x ${item.metalContent[metalContent]} ozs = ${this.toCurrency(
          ozSpot
        )}</div>`
        // Premium Line
        let premiumLine = ``
        let computedPremiumBid = ozSpot * (premium / 100)
        switch (priceFormat) {
          case '$':
            premiumLine += `<div>Premium: ${this.toCurrency(premium)}/oz x ${
              item.metalContent[metalContent]
            } ozs = ${this.toCurrency(
              premium * item.metalContent[metalContent]
            )}</div>`
            break
          case '%':
            premiumLine += `<div>Premium: ${premium}% of ${this.toCurrency(
              ozSpot
            )} = ${this.toCurrency(computedPremiumBid)}</div>`
        }
        return spotLine.concat(premiumLine)
      } else {
        return `<div>(Fixed Price)</div>`
      }
    },
    fixZeroes(display) {
      if (parseFloat(display) - parseInt(display) === 0)
        return parseInt(display)
      return display
    },
    setDecimals(num, min, max) {
      return Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
      }).format(num)
    },
    getMin(item, buySell) {
      return this.fixZeroes(
        Math.max(
          ...[
            item[`min${buySell === 'buy' ? 'Ask' : 'Bid'}Qty`] || null,
            item.minQuantity || null,
            1,
          ].filter((v) => v !== null)
        )
      )
    },
    getIncrementQty(item) {
      const incrementQty = Number(item?.incrementQty ?? item?.minIncrementQty)
      if (!Number.isFinite(incrementQty) || incrementQty <= 0) return null
      return incrementQty
    },
    getDefaultQty(item, buySell) {
      const minQty = Number(this.getMin(item, buySell))
      const incrementQty = this.getIncrementQty(item)
      if (!incrementQty) return this.fixZeroes(minQty)
      const normalizedQty = Math.ceil(minQty / incrementQty) * incrementQty
      return this.fixZeroes(normalizedQty)
    },
    getNextIncrementQty(item, currentQty, buySell) {
      const incrementQty = this.getIncrementQty(item)
      if (!incrementQty) return this.fixZeroes(Number(currentQty) + 1)
      const minQty = Number(this.getDefaultQty(item, buySell))
      const normalizedCurrentQty = Number(currentQty)
      if (!Number.isFinite(normalizedCurrentQty) || normalizedCurrentQty < minQty) {
        return this.fixZeroes(minQty)
      }
      return this.fixZeroes(normalizedCurrentQty + incrementQty)
    },
    matchesIncrement(item, qty) {
      const incrementQty = this.getIncrementQty(item)
      if (!incrementQty) return true
      const quantity = this.fixNumber(qty, 8)
      if (!Number.isFinite(quantity)) return true
      return quantity % incrementQty === 0
    },
    getMax(item, buySell) {
      if (buySell === `buy`) {
        return this.fixZeroes(item.activelySelling ? item.sellQuantity : 0)
      } else {
        return this.fixZeroes(item.activelyBuying ? item.buyQuantity : 0)
      }
    },
    getRulesForItem(item, buySell, ignoreMax = false) {
      const max = this.getMax(item, buySell)
      const min = this.getMin(item, buySell)
      return [
        () => ignoreMax || max > 0 || 'This item is no longer available',
        (v) => !!v || 'This field is required',
        (v) => this.fixNumber(v) > 0 || 'Must be at least one',
        (v) => ignoreMax || this.fixNumber(v) <= max || 'Max: ' + max,
        (v) => this.fixNumber(v) >= min || 'Min: ' + min,
        (v) => this.matchesIncrement(item, v) || `Must be in increments of ${this.fixZeroes(this.getIncrementQty(item))}`,
        (v) => this.allowFractional(item, v),
      ]
    },
    allowFractional(item, v) {
      if (
        item.allowFractionalQty !== true &&
        parseFloat(this.fixNumber(v)) - parseInt(this.fixNumber(v)) !== 0
      )
        return 'Must be a whole number'
      else return true
    },
    getMaxAllowedQtyDecimals(product) {
      if (!product.allowFractionalQty) return 0;
      const primary = this.getMetalPrimaryContent(product)
      if (primary === 'silver') return 1
      if (['gold', 'platinum', 'palladium'].includes(primary)) return 3

      const sym = (product.metalSymbol || product.metal_symbol || '').toString().toUpperCase()
      if (sym === 'AG' || sym === 'XAG') return 1
      if (['AU', 'XAU', 'PT', 'XPT', 'PD', 'XPD'].includes(sym)) return 3

      const type = (product.metalType || product.metal_type || '').toString().toLowerCase()
      if (type.includes('silver')) return 1
      if (type.includes('gold') || type.includes('platinum') || type.includes('palladium')) return 3
      return 4
    },
  },
}
